import React, { useState, useEffect } from 'react';
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle,
  CardDescription 
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { toast } from '@/components/ui/use-toast';
import { 
  Search, 
  CheckCircle, 
  XCircle, 
  Clock,
  MoreHorizontal,
  Loader2,
  ListChecks,
  Filter,
  Trash2,
  FlaskConical,
  FileText,
  Plus,
  Globe
} from 'lucide-react';
import { ScrollArea } from '../components/ui/scroll-area';
import { API_URL } from '../config/api';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { useAuth } from '@/context/auth-context';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useSite } from '@/contexts/SiteContext';

interface Site {
  id: string;
  url: string;
  name: string;
  sector_ids?: number[];
}

interface Sector {
  id: number;
  name: string;
}

interface Topic {
  id: number;
  sector_id: number;
  site_id?: string;
  topic_name: string;
  topic_description: string;
  relevance_score: number;
  status: 'pending' | 'approved' | 'rejected' | 'hold' | 'researching' | 'completed';
  generation_metadata?: any;
  created_at: string;
  updated_at: string;
  sector_name?: string;
  site_name?: string;
  site_url?: string;
  has_research?: boolean;
  research_created_at?: string;
  research_quality_score?: number;
}

interface ResearchDocument {
  id: number;
  topic_id: number;
  topic_name: string;
  topic_description: string;
  sector_name: string;
  research_content: string;
  research_summary?: string;
  key_findings?: any;
  sources?: any;
  research_quality_score?: number;
  suggested_followup_questions?: string[];
  created_at: string;
}

interface ReviewDialogData {
  topic: Topic | null;
  action: 'approve' | 'reject' | 'hold' | 'request_more' | null;
}

export default function MySiteTopics() {
  const { user, token } = useAuth();
  const { selectedSite } = useSite();
  const [topics, setTopics] = useState<Topic[]>([]);
  const [sectors, setSectors] = useState<Sector[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sectorFilter, setSectorFilter] = useState<string>('all');
  const [reviewDialog, setReviewDialog] = useState<ReviewDialogData>({ topic: null, action: null });
  const [reviewNotes, setReviewNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [selectedTopics, setSelectedTopics] = useState<Set<number>>(new Set());
  const [showAddTopicDialog, setShowAddTopicDialog] = useState(false);
  const [newTopic, setNewTopic] = useState({ 
    topic_name: '', 
    topic_description: '', 
    sector_id: '' 
  });
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState<{ open: boolean; topicIds: number[] }>({ open: false, topicIds: [] });
  const [researchDialog, setResearchDialog] = useState<{ open: boolean; topicId: number | null; research: ResearchDocument | null }>({ open: false, topicId: null, research: null });
  const [loadingResearch, setLoadingResearch] = useState(false);

  // Fetch user's sites
  const { data: sites } = useQuery<Site[]>({
    queryKey: ['my-sites'],
    queryFn: async () => {
      const response = await axios.get(`${API_URL}/sites/my-sites`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // API returns {sites: [...], isSuperAdmin: boolean, totalCount: number}
      return Array.isArray(response.data?.sites) ? response.data.sites :
             Array.isArray(response.data) ? response.data : [];
    },
    enabled: !!token
  });

  // Fetch topics for selected site
  const fetchTopics = async () => {
    if (!selectedSite) {
      setTopics([]);
      return;
    }
    
    const siteId = selectedSite._id || selectedSite.id;

    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('site_id', siteId);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (sectorFilter !== 'all') params.append('sector_id', sectorFilter);
      
      const response = await fetch(`${API_URL}/topics?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (response.status === 401) {
        localStorage.removeItem('auth_token');
        window.location.href = '/login';
        return;
      }
      
      if (!response.ok) {
        throw new Error('Failed to fetch topics');
      }
      
      const data = await response.json();
      console.log('Topics API response:', data);
      setTopics(Array.isArray(data) ? data : data.topics || []);
    } catch (error) {
      console.error('Error fetching topics:', error);
      toast({
        title: 'Error',
        description: 'Failed to load topics',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch sectors
  const fetchSectors = async () => {
    try {
      const response = await fetch(`${API_URL}/sectors`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (response.status === 401) {
        localStorage.removeItem('auth_token');
        window.location.href = '/login';
        return;
      }
      
      if (!response.ok) {
        throw new Error('Failed to fetch sectors');
      }
      
      const data = await response.json();
      console.log('Sectors API response:', data);
      // API returns sectors directly as an array, not wrapped in an object
      setSectors(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching sectors:', error);
    }
  };

  useEffect(() => {
    fetchSectors();
  }, []);

  useEffect(() => {
    fetchTopics();
  }, [selectedSite, statusFilter, sectorFilter]);

  // Handle topic review
  const handleReview = async () => {
    if (!reviewDialog.topic || !reviewDialog.action) return;
    
    try {
      setSubmitting(true);
      const response = await fetch(`${API_URL}/topics/${reviewDialog.topic.id}/review`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          action: reviewDialog.action,
          notes: reviewNotes,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to review topic');
      }

      toast({
        title: 'Success',
        description: `Topic ${reviewDialog.action === 'approve' ? 'approved' : 
                     reviewDialog.action === 'reject' ? 'rejected' : 
                     reviewDialog.action === 'hold' ? 'put on hold' : 
                     'marked for more research'} successfully`,
      });
      
      setReviewDialog({ topic: null, action: null });
      setReviewNotes('');
      fetchTopics();
    } catch (error) {
      console.error('Error reviewing topic:', error);
      toast({
        title: 'Error',
        description: 'Failed to review topic',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Handle single topic deletion
  const handleDeleteTopic = async (topicId: number) => {
    setDeleteConfirmDialog({ open: true, topicIds: [topicId] });
  };

  // Handle bulk deletion
  const handleBulkDelete = () => {
    if (selectedTopics.size === 0) {
      toast({
        title: 'No topics selected',
        description: 'Please select topics to delete',
        variant: 'destructive',
      });
      return;
    }
    setDeleteConfirmDialog({ open: true, topicIds: Array.from(selectedTopics) });
  };

  // Confirm deletion
  const confirmDelete = async () => {
    try {
      setSubmitting(true);
      const { topicIds } = deleteConfirmDialog;
      
      if (topicIds.length === 1) {
        // Single delete
        const response = await fetch(`${API_URL}/topics/${topicIds[0]}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        
        if (!response.ok) {
          throw new Error('Failed to delete topic');
        }
      } else {
        // Bulk delete
        const response = await fetch(`${API_URL}/topics/bulk-delete`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({ ids: topicIds }),
        });
        
        if (!response.ok) {
          throw new Error('Failed to delete topics');
        }
      }

      toast({
        title: 'Success',
        description: `${topicIds.length} topic${topicIds.length > 1 ? 's' : ''} deleted successfully`,
      });
      
      setSelectedTopics(new Set());
      setDeleteConfirmDialog({ open: false, topicIds: [] });
      fetchTopics();
    } catch (error) {
      console.error('Error deleting topics:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete topics',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Handle adding a new topic
  const handleAddTopic = async () => {
    if (!newTopic.topic_name || !newTopic.sector_id || !selectedSite) {
      toast({
        title: 'Error',
        description: 'Please fill in all required fields and select a site',
        variant: 'destructive',
      });
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/topics`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          ...newTopic,
          sector_id: parseInt(newTopic.sector_id),
          site_id: selectedSite._id || selectedSite.id,
          relevance_score: 0.8 // Default high relevance for manually added topics
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to add topic');
      }

      toast({
        title: 'Success',
        description: 'Topic added successfully and is pending approval',
      });

      // Reset form and close dialog
      setNewTopic({ topic_name: '', topic_description: '', sector_id: '' });
      setShowAddTopicDialog(false);
      
      // Refresh topics list
      fetchTopics();
    } catch (error) {
      console.error('Error adding topic:', error);
      toast({
        title: 'Error',
        description: 'Failed to add topic',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Toggle selection
  const toggleSelection = (topicId: number) => {
    const newSelection = new Set(selectedTopics);
    if (newSelection.has(topicId)) {
      newSelection.delete(topicId);
    } else {
      newSelection.add(topicId);
    }
    setSelectedTopics(newSelection);
  };

  // Toggle all selections
  const toggleAllSelections = () => {
    if (selectedTopics.size === filteredTopics.length) {
      setSelectedTopics(new Set());
    } else {
      setSelectedTopics(new Set(filteredTopics.map(t => t.id)));
    }
  };

  // Handle bulk approval
  const handleBulkApprove = async () => {
    if (selectedTopics.size === 0) {
      toast({
        title: 'No topics selected',
        description: 'Please select topics to approve',
        variant: 'destructive',
      });
      return;
    }
    
    try {
      setSubmitting(true);
      const response = await fetch(`${API_URL}/topics/bulk-approve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ 
          ids: Array.from(selectedTopics),
          notes: 'Bulk approval'
        }),
      });
      
      if (!response.ok) {
        throw new Error('Failed to approve topics');
      }
      
      const data = await response.json();
      toast({
        title: 'Success',
        description: data.message,
      });
      
      setSelectedTopics(new Set());
      fetchTopics();
    } catch (error) {
      console.error('Error approving topics:', error);
      toast({
        title: 'Error',
        description: 'Failed to approve topics',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Handle single topic research
  const handleTriggerResearch = async (topicId: number) => {
    try {
      const response = await fetch(`${API_URL}/topics/${topicId}/research`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to trigger research');
      }
      
      toast({
        title: 'Research Started',
        description: 'Deep research has been triggered for this topic',
      });
      
      fetchTopics();
    } catch (error) {
      console.error('Error triggering research:', error);
      toast({
        title: 'Error',
        description: 'Failed to trigger research',
        variant: 'destructive',
      });
    }
  };

  // Handle bulk research
  const handleBulkResearch = async () => {
    if (selectedTopics.size === 0) {
      toast({
        title: 'No topics selected',
        description: 'Please select topics to research',
        variant: 'destructive',
      });
      return;
    }
    
    try {
      setSubmitting(true);
      const response = await fetch(`${API_URL}/topics/bulk-research`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ ids: Array.from(selectedTopics) }),
      });
      
      if (!response.ok) {
        throw new Error('Failed to trigger research');
      }
      
      const data = await response.json();
      toast({
        title: 'Research Started',
        description: data.message,
      });
      
      setSelectedTopics(new Set());
      fetchTopics();
    } catch (error) {
      console.error('Error triggering bulk research:', error);
      toast({
        title: 'Error',
        description: 'Failed to trigger research',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  // View research document
  const handleViewResearch = async (topicId: number) => {
    try {
      setLoadingResearch(true);
      setResearchDialog({ open: true, topicId, research: null });
      
      const response = await fetch(`${API_URL}/topics/${topicId}/research`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch research');
      }
      
      const data = await response.json();
      setResearchDialog({ open: true, topicId, research: data.research });
    } catch (error) {
      console.error('Error fetching research:', error);
      toast({
        title: 'Error',
        description: 'Failed to load research document',
        variant: 'destructive',
      });
      setResearchDialog({ open: false, topicId: null, research: null });
    } finally {
      setLoadingResearch(false);
    }
  };

  // Delete research document
  const handleDeleteResearch = async () => {
    if (!researchDialog.topicId) return;
    
    try {
      setSubmitting(true);
      const response = await fetch(`${API_URL}/topics/${researchDialog.topicId}/research`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete research');
      }
      
      toast({
        title: 'Success',
        description: 'Research document deleted successfully',
      });
      
      setResearchDialog({ open: false, topicId: null, research: null });
      fetchTopics();
    } catch (error) {
      console.error('Error deleting research:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete research document',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Filter topics based on search
  const filteredTopics = topics.filter(topic => 
    topic.topic_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    topic.topic_description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getStatusBadge = (status: string) => {
    const variants: Record<string, { variant: 'default' | 'secondary' | 'destructive' | 'outline', icon?: any }> = {
      pending: { variant: 'secondary', icon: Clock },
      approved: { variant: 'default', icon: CheckCircle },
      rejected: { variant: 'destructive', icon: XCircle },
      hold: { variant: 'outline', icon: Clock },
      researching: { variant: 'secondary', icon: MoreHorizontal },
      completed: { variant: 'default', icon: CheckCircle },
    };
    
    const config = variants[status] || { variant: 'outline' };
    const Icon = config.icon;
    
    return (
      <Badge variant={config.variant} className="flex items-center gap-1">
        {Icon && <Icon className="h-3 w-3" />}
        {status}
      </Badge>
    );
  };

  const getScoreBadge = (score: number) => {
    const variant = score >= 0.7 ? 'default' : score >= 0.4 ? 'secondary' : 'outline';
    return (
      <Badge variant={variant}>
        {(score * 100).toFixed(0)}%
      </Badge>
    );
  };

  // Get selected site sector IDs
  const selectedSiteSectorIds = selectedSite ? 
    sites?.find(s => s.id === (selectedSite._id || selectedSite.id))?.sector_ids || [] : [];
  
  // Debug logging
  console.log('Debug MySiteTopics:', {
    selectedSite,
    sites,
    selectedSiteSectorIds,
    sectors: sectors.length,
    sectorsData: sectors.slice(0, 3)
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">My Site Topics</h1>
          <p className="text-muted-foreground">
            Manage and review topics for your sites
          </p>
        </div>
        <Button onClick={() => setShowAddTopicDialog(true)} disabled={!selectedSite}>
          <Plus className="mr-2 h-4 w-4" />
          Add Topic
        </Button>
      </div>

      {/* Site info from global context */}
      {selectedSite ? (
        <Card>
          <CardHeader>
            <CardTitle>Managing Topics for:</CardTitle>
            <CardDescription className="flex items-center gap-2">
              <Globe className="w-4 h-4" />
              {selectedSite.name || selectedSite.url}
            </CardDescription>
          </CardHeader>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>No Site Selected</CardTitle>
            <CardDescription>Please select a site from the header to manage topics</CardDescription>
          </CardHeader>
        </Card>
      )}

      {selectedSite && (
        <>
          {/* Stats Cards */}
          <div className="grid gap-4 md:grid-cols-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Pending Review</CardTitle>
                <Clock className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {topics.filter(t => t.status === 'pending').length}
                </div>
                <p className="text-xs text-muted-foreground">
                  Topics awaiting approval
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Approved</CardTitle>
                <CheckCircle className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {topics.filter(t => t.status === 'approved' || t.status === 'completed').length}
                </div>
                <p className="text-xs text-muted-foreground">
                  Ready for research
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">On Hold</CardTitle>
                <Clock className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {topics.filter(t => t.status === 'hold' || t.status === 'researching').length}
                </div>
                <p className="text-xs text-muted-foreground">
                  Requiring attention
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Topics</CardTitle>
                <ListChecks className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{topics.length}</div>
                <p className="text-xs text-muted-foreground">
                  For this site
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Topics Table */}
          <Card>
            <CardHeader>
              <CardTitle>Topics</CardTitle>
              <CardDescription>
                Review and manage topics for {sites?.find(s => s.id === (selectedSite?._id || selectedSite?.id))?.name || 'your site'}
              </CardDescription>
              <div className="flex items-center gap-4 pt-4">
                <div className="flex items-center space-x-2 flex-1">
                  <Search className="h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search topics..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="max-w-sm"
                  />
                </div>
                
                {selectedTopics.size > 0 && (
                  <div className="flex gap-2">
                    <Button
                      variant="default"
                      size="sm"
                      onClick={handleBulkApprove}
                    >
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Approve ({selectedTopics.size})
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={handleBulkResearch}
                    >
                      <FlaskConical className="h-4 w-4 mr-2" />
                      Research ({selectedTopics.size})
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={handleBulkDelete}
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete ({selectedTopics.size})
                    </Button>
                  </div>
                )}
                
                <div className="flex items-center gap-2">
                  <Filter className="h-4 w-4 text-muted-foreground" />
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-[150px]">
                      <SelectValue placeholder="Filter by status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Statuses</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="approved">Approved</SelectItem>
                      <SelectItem value="rejected">Rejected</SelectItem>
                      <SelectItem value="hold">On Hold</SelectItem>
                      <SelectItem value="researching">Researching</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                    </SelectContent>
                  </Select>
                  
                  <Select value={sectorFilter} onValueChange={setSectorFilter}>
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder="Filter by sector" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Sectors</SelectItem>
                      {sectors
                        .filter(sector => selectedSiteSectorIds.includes(sector.id))
                        .map(sector => (
                          <SelectItem key={sector.id} value={sector.id.toString()}>
                            {sector.name}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin" />
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[50px]">
                        <Checkbox
                          checked={filteredTopics.length > 0 && selectedTopics.size === filteredTopics.length}
                          onCheckedChange={toggleAllSelections}
                        />
                      </TableHead>
                      <TableHead>Topic</TableHead>
                      <TableHead>Sector</TableHead>
                      <TableHead>Relevance</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredTopics.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center text-muted-foreground">
                          No topics found
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredTopics.map((topic) => (
                        <TableRow key={topic.id}>
                          <TableCell>
                            <Checkbox
                              checked={selectedTopics.has(topic.id)}
                              onCheckedChange={() => toggleSelection(topic.id)}
                            />
                          </TableCell>
                          <TableCell>
                            <div className="max-w-md">
                              <div className="font-medium">{topic.topic_name}</div>
                              {topic.topic_description && (
                                <div className="text-sm text-muted-foreground line-clamp-2">
                                  {topic.topic_description}
                                </div>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">
                              {topic.sector_name || 'Unknown'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {getScoreBadge(topic.relevance_score)}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {getStatusBadge(topic.status)}
                              {topic.has_research && (
                                <Badge variant="outline" className="text-xs">
                                  <FileText className="h-3 w-3 mr-1" />
                                  Researched
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            {new Date(topic.created_at).toLocaleDateString()}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              {topic.status === 'pending' && (
                                <>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-green-600 hover:text-green-700"
                                    onClick={() => setReviewDialog({ topic, action: 'approve' })}
                                  >
                                    <CheckCircle className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-red-600 hover:text-red-700"
                                    onClick={() => setReviewDialog({ topic, action: 'reject' })}
                                  >
                                    <XCircle className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => setReviewDialog({ topic, action: 'hold' })}
                                  >
                                    <Clock className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => setReviewDialog({ topic, action: 'request_more' })}
                                  >
                                    <MoreHorizontal className="h-4 w-4" />
                                  </Button>
                                </>
                              )}
                              {(topic.status === 'hold' || topic.status === 'researching') && (
                                <>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-green-600 hover:text-green-700"
                                    onClick={() => setReviewDialog({ topic, action: 'approve' })}
                                  >
                                    <CheckCircle className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-red-600 hover:text-red-700"
                                    onClick={() => setReviewDialog({ topic, action: 'reject' })}
                                  >
                                    <XCircle className="h-4 w-4" />
                                  </Button>
                                </>
                              )}
                              {topic.has_research ? (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-purple-600 hover:text-purple-700"
                                  onClick={() => handleViewResearch(topic.id)}
                                  title="View research document"
                                >
                                  <FileText className="h-4 w-4" />
                                </Button>
                              ) : (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-blue-600 hover:text-blue-700"
                                  onClick={() => handleTriggerResearch(topic.id)}
                                  title="Trigger deep research"
                                >
                                  <FlaskConical className="h-4 w-4" />
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-red-600 hover:text-red-700"
                                onClick={() => handleDeleteTopic(topic.id)}
                                title="Delete topic"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* Review Dialog */}
      <Dialog open={!!reviewDialog.topic} onOpenChange={(open) => !open && setReviewDialog({ topic: null, action: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {reviewDialog.action === 'approve' && 'Approve Topic'}
              {reviewDialog.action === 'reject' && 'Reject Topic'}
              {reviewDialog.action === 'hold' && 'Put Topic on Hold'}
              {reviewDialog.action === 'request_more' && 'Request More Research'}
            </DialogTitle>
            <DialogDescription>
              {reviewDialog.topic?.topic_name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Topic Description</Label>
              <p className="text-sm text-muted-foreground mt-1">
                {reviewDialog.topic?.topic_description || 'No description provided'}
              </p>
            </div>
            
            <div className="flex gap-4">
              <div>
                <Label>Sector</Label>
                <p className="text-sm font-medium mt-1">
                  {reviewDialog.topic?.sector_name || 'Unknown'}
                </p>
              </div>
              <div>
                <Label>Relevance Score</Label>
                <p className="text-sm font-medium mt-1">
                  {((reviewDialog.topic?.relevance_score || 0) * 100).toFixed(0)}%
                </p>
              </div>
            </div>
            
            <div>
              <Label htmlFor="notes">Review Notes</Label>
              <Textarea
                id="notes"
                placeholder={
                  reviewDialog.action === 'reject' ? 'Reason for rejection...' :
                  reviewDialog.action === 'hold' ? 'Reason for holding...' :
                  reviewDialog.action === 'request_more' ? 'What additional research is needed...' :
                  'Optional notes...'
                }
                value={reviewNotes}
                onChange={(e) => setReviewNotes(e.target.value)}
                className="mt-1"
                rows={4}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setReviewDialog({ topic: null, action: null });
                setReviewNotes('');
              }}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleReview}
              disabled={submitting || (reviewDialog.action !== 'approve' && !reviewNotes)}
              variant={reviewDialog.action === 'reject' ? 'destructive' : 'default'}
            >
              {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {reviewDialog.action === 'approve' && 'Approve'}
              {reviewDialog.action === 'reject' && 'Reject'}
              {reviewDialog.action === 'hold' && 'Put on Hold'}
              {reviewDialog.action === 'request_more' && 'Request Research'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmDialog.open} onOpenChange={(open) => !open && setDeleteConfirmDialog({ open: false, topicIds: [] })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Deletion</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete {deleteConfirmDialog.topicIds.length} topic{deleteConfirmDialog.topicIds.length > 1 ? 's' : ''}? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setDeleteConfirmDialog({ open: false, topicIds: [] })}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button 
              variant="destructive"
              onClick={confirmDelete}
              disabled={submitting}
            >
              {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Delete {deleteConfirmDialog.topicIds.length > 1 ? 'Topics' : 'Topic'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Research Document Dialog */}
      <Dialog open={researchDialog.open} onOpenChange={(open) => !open && setResearchDialog({ open: false, topicId: null, research: null })}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Research Document</DialogTitle>
            <DialogDescription>
              {researchDialog.research?.topic_name || 'Loading...'}
            </DialogDescription>
          </DialogHeader>

          {loadingResearch ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : researchDialog.research ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <Label>Sector</Label>
                  <p className="font-medium">{researchDialog.research.sector_name}</p>
                </div>
                <div>
                  <Label>Research Date</Label>
                  <p className="font-medium">
                    {new Date(researchDialog.research.created_at).toLocaleString()}
                  </p>
                </div>
                {researchDialog.research.research_quality_score && (
                  <div>
                    <Label>Quality Score</Label>
                    <p className="font-medium">
                      {(researchDialog.research.research_quality_score * 100).toFixed(0)}%
                    </p>
                  </div>
                )}
              </div>
              
              {researchDialog.research.research_summary && (
                <div>
                  <Label>Summary</Label>
                  <p className="text-sm text-muted-foreground mt-1">
                    {researchDialog.research.research_summary}
                  </p>
                </div>
              )}
              
              <div>
                <Label>Research Content</Label>
                <ScrollArea className="h-[400px] mt-2 border border-border rounded-md p-4 bg-muted/30">
                  <div className="prose prose-sm dark:prose-invert max-w-none">
                    <pre className="whitespace-pre-wrap font-sans text-foreground">
                      {researchDialog.research.research_content}
                    </pre>
                  </div>
                </ScrollArea>
              </div>

              {researchDialog.research.key_findings && (
                <div>
                  <Label>Key Findings</Label>
                  <div className="mt-2 space-y-2">
                    {Array.isArray(researchDialog.research.key_findings) ?
                      researchDialog.research.key_findings.map((finding: any, index: number) => (
                        <div key={index} className="text-sm bg-muted p-2 rounded">
                          {finding}
                        </div>
                      )) :
                      <pre className="text-sm bg-muted p-2 rounded">
                        {JSON.stringify(researchDialog.research.key_findings, null, 2)}
                      </pre>
                    }
                  </div>
                </div>
              )}

              {researchDialog.research.suggested_followup_questions && researchDialog.research.suggested_followup_questions.length > 0 && (
                <div>
                  <Label>Suggested Follow-up Questions</Label>
                  <div className="mt-2 space-y-2">
                    {researchDialog.research.suggested_followup_questions.map((question: string, index: number) => (
                      <div key={index} className="text-sm bg-blue-50 dark:bg-blue-950 p-2 rounded flex items-start gap-2">
                        <span className="text-blue-600 dark:text-blue-400 font-semibold">{index + 1}.</span>
                        <span>{question}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : null}
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setResearchDialog({ open: false, topicId: null, research: null })}
            >
              Close
            </Button>
            {researchDialog.research && (
              <Button 
                variant="destructive"
                onClick={handleDeleteResearch}
                disabled={submitting}
              >
                {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Delete Research
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Topic Dialog */}
      <Dialog open={showAddTopicDialog} onOpenChange={setShowAddTopicDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add New Topic</DialogTitle>
            <DialogDescription>
              Add a new topic for your site. It will be automatically approved and queued for research.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="topic-name">Topic Name *</Label>
              <Input
                id="topic-name"
                placeholder="e.g., Product Features"
                value={newTopic.topic_name}
                onChange={(e) => setNewTopic({ ...newTopic, topic_name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="topic-description">Topic Description</Label>
              <Textarea
                id="topic-description"
                placeholder="Brief description of the topic..."
                rows={3}
                value={newTopic.topic_description}
                onChange={(e) => setNewTopic({ ...newTopic, topic_description: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="sector">Sector *</Label>
              <Select
                value={newTopic.sector_id}
                onValueChange={(value) => setNewTopic({ ...newTopic, sector_id: value })}
              >
                <SelectTrigger id="sector">
                  <SelectValue placeholder="Select a sector" />
                </SelectTrigger>
                <SelectContent>
                  {sectors
                    .filter(sector => selectedSiteSectorIds.includes(sector.id))
                    .map((sector) => (
                      <SelectItem key={sector.id} value={sector.id.toString()}>
                        {sector.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setShowAddTopicDialog(false);
                setNewTopic({ topic_name: '', topic_description: '', sector_id: '' });
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleAddTopic}
              disabled={submitting}
            >
              {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Add Topic
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}